import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import authService from '../services/authService';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const doRestore = async () => {
      try {
        // Attempt silent session restore via httpOnly refresh cookie
        const user = await authService.refresh();
        if (!mounted) return;
        setIsAuthenticated(true);
        setCurrentUser(user);
      } catch (err) {
        if (err && err.name === 'AbortError') {
          logger.warn('Auth refresh timed out');
        }
        // No valid refresh cookie — user must log in
        if (!mounted) return;
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    doRestore();

    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(async (username, password) => {
    setError(null);
    const user = await authService.login(username, password);
    setIsAuthenticated(true);
    setCurrentUser(user);
    return user;
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setCurrentUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, currentUser, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Fallback for tests or components rendered without provider
    return {
      isAuthenticated: false,
      currentUser: null,
      loading: false,
      error: null,
      login: async () => { throw new Error('useAuth used outside AuthProvider'); },
      logout: async () => {},
    };
  }
  return ctx;
}

export default AuthProvider;
